import { FC, FormEvent, useEffect, useRef, useState } from "react";
import { CODE_LENGTH, maskEmail } from "utils/trackBooking";
import { trackBookingStep, trackBookingStepError } from "analytics/bookingEvents";
import EmailCodeInput, { buttonCls, clock, linkButtonCls } from "./EmailCodeInput";
import { CodeGate, resendEmailCode } from "./emailCode";

/**
 * "Check your email" (1NOW-10770): a returning renter's six-digit code, in Track my booking's code card. It knows
 * nothing of the booking: the host (the Pay step, the dialog on the car page, the Reserved page) says what a right
 * code does (`onVerify`) and where "Use a different email" goes. Six digits are checked at once (or with the
 * button); a refused code shows the backend's own words with red boxes, and "Resend code" works after the wait.
 */
const EmailCodeBlock: FC<CodeGate & { submitLabel?: string; heading?: boolean }> = ({
  customer,
  resendAfter,
  error,
  onVerify,
  onDifferentEmail,
  submitLabel = "Continue",
  heading = true,
}) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(error ?? "");
  const [bad, setBad] = useState(false);
  const [notice, setNotice] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(resendAfter ?? 0);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    codeRef.current?.focus();
    // 1NOW-11501: the code question is a step of its own in the funnel, wherever it is asked (Pay, the dialog, the Reserved page).
    trackBookingStep("email_code", { returning_renter: true });
  }, []);

  // The resend wait counts down while the box is open.
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const verify = async (value: string) => {
    if (busy) return;
    if (value.length !== CODE_LENGTH) {
      setProblem(`Enter the ${CODE_LENGTH}-digit code.`);
      return;
    }
    setProblem("");
    setNotice("");
    setBad(false);
    setBusy(true);
    const result = await onVerify(value);
    setBusy(false);
    if ("error" in result) {
      setProblem(result.error);
      setBad(Boolean(result.bad));
      trackBookingStepError("email_code", result.error);
    }
  };

  const resend = async () => {
    if (busy || secondsLeft > 0) return;
    setProblem("");
    setBad(false);
    setNotice("");
    setBusy(true);
    const result = await resendEmailCode(customer);
    setBusy(false);
    if ("error" in result) {
      setProblem(result.error);
      trackBookingStepError("email_code", result.error);
      if (result.resendAfter) setSecondsLeft(result.resendAfter);
    } else {
      setCode("");
      setSecondsLeft(result.resendAfter);
      setNotice(result.notice);
    }
  };

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void verify(code);
      }}
      noValidate
      className="ph-no-capture text-text-color"
      data-testid="email-code"
    >
      {heading && <h3 className="text-lg font-extrabold">Check your email</h3>}
      <p className={`${heading ? "mt-1 " : ""}mb-4 text-sm leading-relaxed text-text-color/70`}>
        You&apos;ve booked with us before. Enter the code we sent to{" "}
        <span className="font-bold text-text-color">{maskEmail(customer.email)}</span>. It works for 10 minutes.
      </p>
      {problem && (
        <div role="alert" className="mb-4 rounded-2xl bg-red-50 p-3.5 text-sm font-bold text-red-800">
          {problem}
        </div>
      )}
      {notice && !problem && (
        <p role="status" className="mb-4 text-sm font-semibold">
          {notice}
        </p>
      )}
      <EmailCodeInput
        id="email-code"
        ref={codeRef}
        value={code}
        onChange={(next) => {
          setCode(next);
          if (bad) setBad(false);
          if (next.length === CODE_LENGTH) void verify(next);
        }}
        invalid={bad}
      />
      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
        <button type="submit" disabled={busy} className={buttonCls}>
          {busy ? (
            <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            submitLabel
          )}
        </button>
        <button type="button" onClick={resend} disabled={busy || secondsLeft > 0} className={linkButtonCls}>
          {secondsLeft > 0 ? `Resend code in ${clock(secondsLeft)}` : "Resend code"}
        </button>
      </div>
      <button type="button" onClick={onDifferentEmail} className={`mt-4 ${linkButtonCls}`}>
        Use a different email
      </button>
    </form>
  );
};

export default EmailCodeBlock;
